import { ACTION_ICONS } from "../../config/siteConfig";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Select from "react-select";
import { Modal } from "react-bootstrap";
import { useLoadingBar } from "react-top-loading-bar";
import { CSVLink } from "react-csv";
import toast from "react-hot-toast";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import ContentLoader from "../../components/loader/ContentLoader";
import DetailView from "../../components/location/DetailView";
import { flightMarkupsService } from "../../services/flight-markups.service";
import { suppliersService } from "../../services/suppliers.service";
import { airlinesService } from "../../services/airlines.service";
import { SOMETHING_WENT_WRONG } from "../../constants/strings";

const toArray = (resData) =>
  Array.isArray(resData) ? resData : resData?.data || [];

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

const ROUTE_TYPE_OPTIONS = [
  { value: "domestic", label: "Domestic" },
  { value: "international", label: "International" },
];

const TRIP_TYPE_OPTIONS = [
  { value: "oneway", label: "One Way" },
  { value: "roundtrip", label: "Round Trip" },
  { value: "multicity", label: "Multi City" },
];

const CABIN_CLASS_OPTIONS = [
  { value: "economic", label: "Economy" },
  { value: "premium_economy", label: "Premium Economy" },
  { value: "business", label: "Business" },
  { value: "first", label: "First" },
];

const MARKUP_TYPE_OPTIONS = [
  { value: "fixed", label: "Fixed" },
  { value: "percentage", label: "Percentage" },
];

const selectStyles = {
  control: (base, state) => ({
    ...base,
    borderRadius: 8,
    border: "1px solid #e8edf5",
    background: "#f8f9fe",
    minHeight: 34,
    height: 34,
    fontSize: 13,
    boxShadow: "none",
    "&:hover": { borderColor: state.isFocused ? "var(--btn-bg)" : "#e8edf5" },
  }),
  valueContainer: (base) => ({ ...base, padding: "0 8px" }),
  indicatorsContainer: (base) => ({ ...base, height: 34 }),
  placeholder: (base) => ({ ...base, color: "#a0a8b8", fontSize: 13 }),
  menuPortal: (base) => ({ ...base, zIndex: 2000 }),
};

const optionLabel = (options, value) =>
  options.find((o) => o.value === value)?.label || value || "-";

const formatAmount = (m) =>
  m?.markup_amount === undefined || m?.markup_amount === null
    ? "-"
    : m?.markup_amount_type === "percentage"
      ? `${m.markup_amount}%`
      : `₹ ${m.markup_amount}`;

const FlightMarkups = () => {
  const { start, complete } = useLoadingBar();

  const [loading, setLoading] = useState(false);
  const [markupRes, setMarkupRes] = useState({
    pagination: {},
    markups: [],
  });

  const [pageValue, setPageValue] = useState(1);
  const [pagingLimit, setPagingLimit] = useState("10");
  const [goPage, setGoPage] = useState("");

  const initialFilters = {
    supplier_code: "",
    airline_code: "",
    route_type: "",
    trip_type: "",
    cabin_class: "",
    markup_amount_type: "",
    status: "",
  };
  const [filters, setFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);

  const [show, setShow] = useState(false);
  const [viewMarkup, setViewMarkup] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);

  const [supplierOptions, setSupplierOptions] = useState([]);
  const [airlineOptions, setAirlineOptions] = useState([]);

  useEffect(() => {
    loadMarkups();
  }, [pagingLimit, pageValue, appliedFilters]);

  useEffect(() => {
    loadSupplierOptions();
    loadAirlineOptions();
  }, []);

  const showSwal = (title, text, icon) => {
    withReactContent(Swal).fire({ title, text, icon });
  };

  const loadSupplierOptions = async () => {
    try {
      const res = await suppliersService.list({
        page: 1,
        status: "active",
      });
      setSupplierOptions(
        toArray(res?.resData).map((s) => ({
          value: s.supplier_code,
          label: `${s.agency_name} (${s.supplier_code})`,
        })),
      );
    } catch (err) {
      setSupplierOptions([]);
    }
  };

  const loadAirlineOptions = async () => {
    try {
      const res = await airlinesService.list({ page: 1, status: "active" });
      setAirlineOptions(
        toArray(res?.resData).map((a) => ({
          value: a.airline_code,
          label: `${a.airline_name} (${a.airline_code})`,
        })),
      );
    } catch (err) {
      setAirlineOptions([]);
    }
  };

  const supplierName = (code) =>
    supplierOptions.find((o) => o.value === code)?.label || code || "-";
  const airlineName = (code) =>
    airlineOptions.find((o) => o.value === code)?.label || code || "-";

  const loadMarkups = async () => {
    try {
      setLoading(true);
      start();
      const res = await flightMarkupsService.list({
        page: Number(pageValue),
        limit: Number(pagingLimit),
        ...appliedFilters,
      });
      if (res?.success) {
        setMarkupRes({
          pagination: res.resData?.pagination || {},
          markups: res.resData?.data || [],
        });
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error");
    } finally {
      setLoading(false);
      complete();
    }
  };

  const handleStatusChange = async (markup, newStatus) => {
    if (newStatus === markup?.status) return;
    const result = await withReactContent(Swal).fire({
      title: "Change status?",
      text: `Are you sure you want to mark this markup for ${markup?.supplier_code} as ${newStatus}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, change it",
      cancelButtonText: "Cancel",
    });
    if (!result.isConfirmed) return;
    try {
      setLoading(true);
      const res = await flightMarkupsService.updateStatus(markup?.id, {
        status: newStatus,
      });
      if (res?.success) {
        toast.success(res.message);
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error");
    } finally {
      setLoading(false);
      loadMarkups();
    }
  };

  const handleDeleteMarkup = async (markup) => {
    const result = await withReactContent(Swal).fire({
      title: "Delete markup?",
      text: `Are you sure you want to delete this markup for ${markup?.supplier_code}? This cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d93025",
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel",
    });
    if (!result.isConfirmed) return;
    try {
      setLoading(true);
      const res = await flightMarkupsService.delete(markup?.id);
      if (res?.success) {
        toast.success(res.message);
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error");
    } finally {
      setLoading(false);
      loadMarkups();
    }
  };

  const handleViewMarkup = async (markupId) => {
    setShow(true);
    setViewMarkup(null);
    try {
      setViewLoading(true);
      const res = await flightMarkupsService.details(markupId);
      if (res?.success) {
        setViewMarkup(res.resData);
      } else {
        toast.error(res?.message);
        setShow(false);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error");
      setShow(false);
    } finally {
      setViewLoading(false);
    }
  };

  // ---- Add / Update Flight Markup (popup) ----
  const initialForm = {
    supplier_code: "",
    airline_code: "",
    route_type: "",
    trip_type: "",
    cabin_class: "",
    markup_amount: "",
    markup_amount_type: "",
    priority: "",
    status: "active",
  };
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  // edit mode: id of the markup being edited (null = add)
  const [editId, setEditId] = useState(null);
  const isEdit = !!editId;

  const openAdd = () => {
    setEditId(null);
    setForm(initialForm);
    setShowForm(true);
  };

  const closeForm = () => {
    if (!saving) setShowForm(false);
  };

  const openEdit = async (markupId) => {
    setEditId(markupId);
    setForm(initialForm);
    setShowForm(true);
    try {
      setSaving(true);
      const res = await flightMarkupsService.details(markupId);
      if (res?.success) {
        const m = res.resData;
        setForm({
          supplier_code: m?.supplier_code || "",
          airline_code: m?.airline_code || "",
          route_type: m?.route_type || "",
          trip_type: m?.trip_type || "",
          cabin_class: m?.cabin_class || "",
          markup_amount: m?.markup_amount ?? "",
          markup_amount_type: m?.markup_amount_type || "",
          priority: m?.priority ?? "",
          status: m?.status || "active",
        });
      } else {
        toast.error(res?.message);
        setShowForm(false);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error");
      setShowForm(false);
    } finally {
      setSaving(false);
    }
  };

  const handleFormChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  // Markup Type + Markup Amount are required, and at least one of
  // Supplier / Airline / Route Type / Trip Type / Cabin Class
  const validateForm = () => {
    if (!form.markup_amount_type) return "Markup type is required";
    if (form.markup_amount === "" || Number(form.markup_amount) < 0)
      return "Markup amount is required";
    if (
      !form.supplier_code &&
      !form.airline_code &&
      !form.route_type &&
      !form.trip_type &&
      !form.cabin_class
    )
      return "Select at least one of Supplier, Airline, Route Type, Trip Type or Cabin Class";
    return null;
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    const err = validateForm();
    if (err) {
      toast.error(err);
      return;
    }
    const confirm = await withReactContent(Swal).fire({
      title: isEdit ? "Update markup?" : "Save markup?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: isEdit ? "Yes, update" : "Yes, save",
      cancelButtonText: "Cancel",
    });
    if (!confirm.isConfirmed) return;
    try {
      setSaving(true);
      const payload = {
        ...(form.supplier_code ? { supplier_code: form.supplier_code } : {}),
        ...(form.airline_code ? { airline_code: form.airline_code } : {}),
        ...(form.route_type ? { route_type: form.route_type } : {}),
        ...(form.trip_type ? { trip_type: form.trip_type } : {}),
        ...(form.cabin_class ? { cabin_class: form.cabin_class } : {}),
        ...(form.markup_amount !== ""
          ? { markup_amount: Number(form.markup_amount) }
          : {}),
        ...(form.markup_amount_type
          ? { markup_amount_type: form.markup_amount_type }
          : {}),
        ...(form.priority !== "" ? { priority: Number(form.priority) } : {}),
        status: form.status,
      };
      const res = isEdit
        ? await flightMarkupsService.update(editId, payload)
        : await flightMarkupsService.create(payload);
      if (res?.success) {
        await withReactContent(Swal).fire({
          title: "Success",
          text: res.message,
          icon: "success",
        });
        setShowForm(false);
        if (isEdit || pageValue === 1) loadMarkups();
        else setPageValue(1);
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleApply = (e) => {
    e?.preventDefault();
    setPageValue(1);
    setAppliedFilters({ ...filters });
  };

  const handleReset = () => {
    setFilters(initialFilters);
    setPageValue(1);
    setAppliedFilters(initialFilters);
  };

  const handleGoPage = () => {
    const n = Number(goPage);
    if (n >= 1 && n <= totalPage) {
      setPageValue(n);
    }
    setGoPage("");
  };

  const handlePagingLimit = (e) => {
    setPageValue(1);
    setPagingLimit(e.target.value);
  };

  const handlePagination = (pageValueInput) => {
    setPageValue(pageValueInput);
  };

  const markups = markupRes?.markups || [];
  const totalRecords = markupRes?.pagination?.total || 0;
  const totalPage = Math.max(1, Math.ceil(totalRecords / Number(pagingLimit)));
  const activeCount = markups.filter((v) => v?.status === "active").length;
  const fromRecord =
    totalRecords === 0 ? 0 : (pageValue - 1) * Number(pagingLimit) + 1;
  const toRecord = Math.min(pageValue * Number(pagingLimit), totalRecords);

  const getPageNumbers = () => {
    if (totalPage <= 5)
      return Array.from({ length: totalPage }, (_, i) => i + 1);
    const sorted = [...new Set([1, 2, totalPage - 1, totalPage, pageValue - 1, pageValue, pageValue + 1])]
      .filter((n) => n >= 1 && n <= totalPage)
      .sort((a, b) => a - b);
    const out = [];
    sorted.forEach((n, i) => {
      if (i > 0 && n - sorted[i - 1] > 1) out.push("...");
      out.push(n);
    });
    return out;
  };

  const csvHeaders = [
    { label: "Supplier Code", key: "supplier_code" },
    { label: "Airline Code", key: "airline_code" },
    { label: "Route Type", key: "route_type" },
    { label: "Trip Type", key: "trip_type" },
    { label: "Cabin Class", key: "cabin_class" },
    { label: "Markup Amount", key: "markup_amount" },
    { label: "Markup Type", key: "markup_amount_type" },
    { label: "Priority", key: "priority" },
    { label: "Status", key: "status" },
  ];

  const pageBtn = (active, disabled) => ({
    minWidth: 32,
    height: 32,
    padding: "0 8px",
    borderRadius: 8,
    border: active ? "1px solid var(--page-active-bg)" : "1px solid #e8edf5",
    background: active ? "var(--page-active-bg)" : "#fff",
    color: active ? "var(--page-active-text)" : disabled ? "#c3cad8" : "#444",
    fontSize: 13,
    fontWeight: 600,
    cursor: disabled ? "default" : "pointer",
  });

  const chip = (color, bg, border) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 14px",
    borderRadius: 10,
    background: bg,
    border: `1px solid ${border}`,
    color,
    fontSize: 13,
  });

  const actionBtn = (bg, color) => ({
    width: 32,
    height: 32,
    borderRadius: 8,
    background: bg,
    color,
    border: "none",
    padding: 0,
  });

  const selectFilter = (options, key, placeholder, col = "col-6 col-md-2") => (
    <div className={col}>
      <Select
        options={options}
        value={options.find((o) => o.value === filters[key]) || null}
        onChange={(o) => setFilters({ ...filters, [key]: o ? o.value : "" })}
        placeholder={placeholder}
        isClearable
        menuPortalTarget={document.body}
        styles={selectStyles}
      />
    </div>
  );

  const labelStyle = { fontWeight: 600, fontSize: 12.5, color: "#334155", marginBottom: 6 };
  const inputStyle = {
    borderRadius: 8,
    border: "1px solid #c3cad6",
    fontSize: 13,
    height: 40,
    background: "#fff",
  };
  const formSelectStyles = {
    ...selectStyles,
    control: (base, state) => ({
      ...base,
      borderRadius: 8,
      border: state.isFocused ? "1px solid var(--btn-bg)" : "1px solid #c3cad6",
      minHeight: 40,
      fontSize: 13,
      boxShadow: state.isFocused ? "0 0 0 3px rgba(14,106,150,.15)" : "none",
      "&:hover": { borderColor: "var(--btn-bg)" },
    }),
    valueContainer: (base) => ({ ...base, padding: "0 10px" }),
    indicatorsContainer: (base) => ({ ...base, height: 40 }),
    option: (base, state) => ({
      ...base,
      fontSize: 13,
      background: state.isSelected
        ? "var(--btn-bg)"
        : state.isFocused
          ? "#e6f1f8"
          : "#fff",
      color: state.isSelected ? "#fff" : "#1f2a44",
    }),
  };
  const field = (text, required, node, cls = "col-12 col-md-6") => (
    <div className={cls}>
      <div style={labelStyle}>
        {text}
        {required && <span className="text-danger"> *</span>}
      </div>
      {node}
    </div>
  );
  const pick = (options, key, placeholder, opts = {}) => (
    <Select
      isDisabled={opts.disabled}
      options={options}
      value={options.find((o) => o.value === form[key]) || null}
      onChange={(o) => setForm({ ...form, [key]: o ? o.value : "" })}
      placeholder={placeholder}
      isClearable={opts.clearable !== false}
      menuPortalTarget={document.body}
      styles={formSelectStyles}
    />
  );
  const numberInput = (key, placeholder) => (
    <input
      type="number"
      min="0"
      className="form-control"
      name={key}
      placeholder={placeholder}
      value={form[key]}
      onChange={handleFormChange}
      style={inputStyle}
    />
  );

  return (
    <>
      <section className="section dashboard">
        <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
          <div>
            <h4
              className="m-0 fw-bold"
              style={{ color: "var(--page-heading)", fontSize: 20 }}
            >
              Flight Markups
            </h4>
            <nav
              className="d-flex align-items-center gap-1 mt-1"
              style={{ fontSize: 12, color: "#475569" }}
            >
              <Link to="/dashboard" style={{ color: "#475569" }}>
                <i className="bi bi-house-door"></i>
              </Link>
              <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
              <Link to="/setting" style={{ color: "#475569" }}>
                Settings
              </Link>
              <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
              <span className="fw-semibold" style={{ color: "#4a5568" }}>
                Markup List
              </span>
            </nav>
          </div>
          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              onClick={openAdd}
              className="btn btn-sm d-flex align-items-center gap-1 text-white px-3"
              style={{
                background: "var(--btn-bg)",
                borderRadius: 8,
                height: 34,
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              <i className="bi bi-plus-lg"></i> Add Flight Markup
            </button>
            <CSVLink
              data={markups}
              headers={csvHeaders}
              filename="flight-markups.csv"
              className="btn btn-sm d-flex align-items-center gap-1 px-3"
              style={{
                background: "#fff",
                border: "1px solid #e8edf5",
                borderRadius: 8,
                height: 34,
                fontSize: 13,
                color: "#444",
              }}
            >
              <i className="bi bi-download"></i> Export
            </CSVLink>
          </div>
        </div>

        <form
          onSubmit={handleApply}
          className="card border-0 shadow-sm mb-3"
          style={{ borderRadius: 14 }}
        >
          <div className="card-body p-3">
            <div className="row g-2 align-items-center">
              {selectFilter(supplierOptions, "supplier_code", "Select Supplier", "col-6 col-md-3")}
              {selectFilter(airlineOptions, "airline_code", "Select Airline", "col-6 col-md-3")}
              {selectFilter(ROUTE_TYPE_OPTIONS, "route_type", "Select Route Type")}
              {selectFilter(TRIP_TYPE_OPTIONS, "trip_type", "Select Trip Type")}
              {selectFilter(CABIN_CLASS_OPTIONS, "cabin_class", "Select Cabin Class")}
              {selectFilter(MARKUP_TYPE_OPTIONS, "markup_amount_type", "Select Markup Type")}
              {selectFilter(STATUS_OPTIONS, "status", "Select Status")}
              <div className="col-12 col-md-auto d-flex gap-2">
                <button
                  type="submit"
                  className="btn btn-sm d-flex align-items-center gap-1 text-white px-3"
                  style={{
                    background: "var(--btn-bg)",
                    borderRadius: 8,
                    height: 34,
                    fontSize: 13,
                    fontWeight: 600,
                  }}
                >
                  <i className="bi bi-search"></i> Apply
                </button>
                <button
                  type="button"
                  onClick={handleReset}
                  className="btn btn-sm d-flex align-items-center gap-1 px-3"
                  style={{
                    background: "#fff",
                    border: "1px solid #e8edf5",
                    borderRadius: 8,
                    height: 34,
                    fontSize: 13,
                    color: "#444",
                  }}
                >
                  <i className="bi bi-arrow-counterclockwise"></i> Reset
                </button>
              </div>
            </div>
          </div>
        </form>

        <div className="d-flex flex-wrap gap-2 mb-3">
          <span style={chip("#e8590c", "#fff4e6", "#ffd8a8")}>
            <i className="bi bi-circle-fill" style={{ fontSize: 7 }}></i>
            <b>{totalRecords}</b> Total Markups
          </span>
          <span style={chip("#0f8a5f", "#e6f7f1", "#b7ebd6")}>
            <i className="bi bi-circle-fill" style={{ fontSize: 7 }}></i>
            <b>{activeCount}</b> Active on this page
          </span>
          <span style={chip("#1971c2", "#e7f5ff", "#a5d8ff")}>
            <i className="bi bi-circle-fill" style={{ fontSize: 7 }}></i>
            <b>{markups.length - activeCount}</b> Inactive on this page
          </span>
        </div>

        <div
          className="card border-0 shadow-sm"
          style={{ borderRadius: 14, overflow: "hidden" }}
        >
          <div style={{ position: "relative" }}>
            {loading && (
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: "rgba(255,255,255,0.6)",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  zIndex: 10,
                }}
              >
                <ContentLoader loaderType="circleSpin" />
              </div>
            )}
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead>
                  <tr style={{ background: "#f8f9fe" }}>
                    {["S.No.", 
                      "Supplier",
                      "Airline",
                      "Route Type",
                      "Trip Type",
                      "Cabin Class",
                      "Markup",
                      "Priority",
                      "Status",
                      "Actions",
                    ].map((h, i) => (
                      <th
                        key={i}
                        style={{
                          fontSize: "11px",
                          fontWeight: "700",
                          color: "#475569",
                          textTransform: "uppercase",
                          letterSpacing: "0.6px",
                          borderBottom: "2px solid #e8edf5",
                          padding: "10px 12px",
                          whiteSpace: "nowrap",
                          background: "#f8f9fe",
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {markups.map((markup, sno_i) => (
                    <tr
                      key={markup?.id}
                      style={{ borderBottom: "1px solid #f0f2f5" }}
                    >
<td className="ps-3" style={{ fontSize: 13 }}>{(pageValue - 1) * Number(pagingLimit) + sno_i + 1}</td>
                      <td className="ps-3">
                        <div className="d-flex align-items-center gap-2">
                          <div
                            className="rounded-circle d-flex align-items-center justify-content-center"
                            style={{
                              width: 36,
                              height: 36,
                              background: "#e8f0fe",
                              flexShrink: 0,
                            }}
                          >
                            <i
                              className="bi bi-person-badge text-primary"
                              style={{ fontSize: 14 }}
                            ></i>
                          </div>
                          <div
                            className="fw-semibold text-dark"
                            style={{ fontSize: 13 }}
                          >
                            {supplierName(markup?.supplier_code)}
                          </div>
                        </div>
                      </td>
                      <td style={{ fontSize: 13 }}>
                        {airlineName(markup?.airline_code)}
                      </td>
                      <td style={{ fontSize: 13 }}>
                        {optionLabel(ROUTE_TYPE_OPTIONS, markup?.route_type)}
                      </td>
                      <td style={{ fontSize: 13 }}>
                        {optionLabel(TRIP_TYPE_OPTIONS, markup?.trip_type)}
                      </td>
                      <td style={{ fontSize: 13 }}>
                        {optionLabel(CABIN_CLASS_OPTIONS, markup?.cabin_class)}
                      </td>
                      <td className="fw-semibold" style={{ fontSize: 13 }}>
                        {formatAmount(markup)}
                      </td>
                      <td style={{ fontSize: 13 }}>
                        {markup?.priority ?? "-"}
                      </td>
                      <td>
                        <select
                          className="form-select form-select-sm"
                          value={markup?.status}
                          onChange={(e) =>
                            handleStatusChange(markup, e.target.value)
                          }
                          style={{
                            width: "auto",
                            minWidth: 0,
                            height: 28,
                            padding: "0 26px 0 10px",
                            backgroundPosition: "right 8px center",
                            borderRadius: 8,
                            fontSize: 12,
                            fontWeight: 600,
                            backgroundColor:
                              markup?.status === "active"
                                ? "#d4edda"
                                : "#f8d7da",
                            color:
                              markup?.status === "active"
                                ? "#155724"
                                : "#721c24",
                            border: "none",
                          }}
                        >
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </select>
                      </td>
                      <td className="text-center">
                        <div className="d-flex align-items-center justify-content-center gap-2">
                          <button
                            className="btn btn-sm d-flex align-items-center justify-content-center"
                            onClick={() => handleViewMarkup(markup?.id)}
                            title="View"
                            style={actionBtn("#e6f7f1", "#0f8a5f")}
                          >
                            <i
                              className={`bi ${ACTION_ICONS.view}`} data-action="view"
                              style={{ fontSize: 13 }}
                            ></i>
                          </button>
                          <button
                            className="btn btn-sm d-flex align-items-center justify-content-center"
                            onClick={() => openEdit(markup?.id)}
                            title="Edit"
                            style={actionBtn("#e8f0fe", "var(--btn-bg)")}
                          >
                            <i
                              className={`bi ${ACTION_ICONS.edit}`} data-action="edit"
                              style={{ fontSize: 13 }}
                            ></i>
                          </button>
                          <button
                            className="btn btn-sm d-flex align-items-center justify-content-center"
                            onClick={() => handleDeleteMarkup(markup)}
                            title="Delete"
                            style={actionBtn("#fce8e6", "#d93025")}
                          >
                            <i
                              className={`bi ${ACTION_ICONS.delete}`} data-action="delete"
                              style={{ fontSize: 13 }}
                            ></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!loading && markups.length === 0 && (
                    <tr>
                      <td
                        colSpan={10}
                        className="text-center text-muted py-4"
                        style={{ fontSize: 13 }}
                      >
                        No markups found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <div
            className="d-flex align-items-center justify-content-between flex-wrap gap-2 px-3 py-3"
            style={{
              borderTop: "1px solid #eef1f6",
              fontSize: 13,
              color: "#6b7a90",
            }}
          >
            <div>
              Showing{" "}
              <b>
                {fromRecord}–{toRecord}
              </b>{" "}
              of <b>{totalRecords}</b> markups
            </div>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <span>Rows</span>
              <select
                className="form-select form-select-sm"
                style={{ width: 100, borderRadius: 8 }}
                value={pagingLimit}
                onChange={handlePagingLimit}
              >
                {["10", "20", "50", "100", "500"].map((v) => (
                  <option key={v} value={v}>
                    {v}/page
                  </option>
                ))}
              </select>
              <span>Go</span>
              <input
                type="number"
                min="1"
                max={totalPage}
                className="form-control form-control-sm text-center"
                style={{ width: 60, borderRadius: 8 }}
                value={goPage}
                onChange={(e) => setGoPage(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleGoPage()}
                placeholder={String(pageValue)}
              />
              <span>/ {totalPage}</span>
              <button
                type="button"
                style={pageBtn(false, false)}
                onClick={handleGoPage}
              >
                Go
              </button>
              <div className="d-flex align-items-center gap-1">
                <button
                  type="button"
                  disabled={pageValue <= 1}
                  style={pageBtn(false, pageValue <= 1)}
                  onClick={() => handlePagination(1)}
                >
                  <i className="bi bi-chevron-double-left"></i>
                </button>
                <button
                  type="button"
                  disabled={pageValue <= 1}
                  style={pageBtn(false, pageValue <= 1)}
                  onClick={() => handlePagination(pageValue - 1)}
                >
                  <i className="bi bi-chevron-left"></i>
                </button>
                {getPageNumbers().map((n, i) =>
                  n === "..." ? (
                    <span key={"e" + i} className="px-1">
                      ...
                    </span>
                  ) : (
                    <button
                      type="button"
                      key={n}
                      style={pageBtn(n === pageValue, false)}
                      onClick={() => handlePagination(n)}
                    >
                      {n}
                    </button>
                  ),
                )}
                <button
                  type="button"
                  disabled={pageValue >= totalPage}
                  style={pageBtn(false, pageValue >= totalPage)}
                  onClick={() => handlePagination(pageValue + 1)}
                >
                  <i className="bi bi-chevron-right"></i>
                </button>
                <button
                  type="button"
                  disabled={pageValue >= totalPage}
                  style={pageBtn(false, pageValue >= totalPage)}
                  onClick={() => handlePagination(totalPage)}
                >
                  <i className="bi bi-chevron-double-right"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        <Modal centered size="lg" show={showForm} onHide={closeForm}>
          <form onSubmit={handleFormSubmit}>
            <Modal.Header closeButton>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700 }}>
                  {isEdit ? "Update" : "Add"} Flight Markup
                </div>
                <div style={{ fontSize: 12, color: "rgba(255,255,255,.78)" }}>
                  {isEdit
                    ? "Update markup information"
                    : "Add a new flight markup to the system"}
                </div>
              </div>
            </Modal.Header>
            <Modal.Body>
              <div className="row g-3">
                <div className="col-12" style={{ fontSize: 12, color: "#475569" }}>
                  <i className="bi bi-info-circle me-1"></i>
                  Select at least one of Supplier, Airline, Route Type, Trip
                  Type or Cabin Class.
                </div>
                {field(
                  "Supplier",
                  false,
                  pick(supplierOptions, "supplier_code", "Select Supplier"),
                )}
                {field(
                  "Airline",
                  false,
                  pick(airlineOptions, "airline_code", "Select Airline"),
                )}
                {field(
                  "Route Type",
                  false,
                  pick(ROUTE_TYPE_OPTIONS, "route_type", "Select Route Type"),
                )}
                {field(
                  "Trip Type",
                  false,
                  pick(TRIP_TYPE_OPTIONS, "trip_type", "Select Trip Type"),
                )}
                {field(
                  "Cabin Class",
                  false,
                  pick(CABIN_CLASS_OPTIONS, "cabin_class", "Select Cabin Class"),
                )}
                {field(
                  "Markup Type",
                  true,
                  pick(MARKUP_TYPE_OPTIONS, "markup_amount_type", "Select Markup Type"),
                )}
                {field("Markup Amount", true, numberInput("markup_amount", "Enter markup amount"))}
                {field("Priority", false, numberInput("priority", "Enter priority"))}
                {field(
                  "Status",
                  false,
                  pick(STATUS_OPTIONS, "status", "Select Status", {
                    clearable: false,
                  }),
                )}
              </div>
            </Modal.Body>
            <Modal.Footer className="justify-content-end gap-2">
              <button
                type="button"
                className="btn"
                onClick={closeForm}
                disabled={saving}
                style={{
                  background: "#fff",
                  border: "1px solid #c3cad6",
                  borderRadius: 8,
                  height: 40,
                  fontSize: 13,
                  color: "#444",
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn text-white fw-semibold"
                disabled={saving}
                style={{
                  background: "var(--btn-bg)",
                  border: "none",
                  borderRadius: 8,
                  height: 40,
                  fontSize: 13,
                }}
              >
                {saving
                  ? "Saving..."
                  : isEdit
                    ? "Update Flight Markup"
                    : "Add Flight Markup"}
              </button>
            </Modal.Footer>
          </form>
        </Modal>

        <Modal size="lg" centered show={show} onHide={() => setShow(false)}>
          <Modal.Header
            closeButton
            closeVariant="white"
            style={{
              background: "linear-gradient(135deg, var(--btn-bg) 0%, var(--btn-hover-bg) 100%)",
              borderBottom: "none",
            }}
          >
            <Modal.Title
              className="d-flex align-items-center gap-2"
              style={{ color: "#fff", fontSize: 18, fontWeight: 600 }}
            >
              <i className={`bi ${ACTION_ICONS.view}`} data-action="view"></i> Markup Details
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ padding: 24, background: "#f8f9fe" }}>
            {viewLoading || !viewMarkup ? (
              <ContentLoader loaderType="circleSpin" />
            ) : (
              <DetailView
                fields={[
                  { label: "Supplier", value: supplierName(viewMarkup.supplier_code) },
                  { label: "Agent Code", value: viewMarkup.agent_code },
                  { label: "Airline", value: airlineName(viewMarkup.airline_code) },
                  { label: "Route Type", value: optionLabel(ROUTE_TYPE_OPTIONS, viewMarkup.route_type) },
                  { label: "Trip Type", value: optionLabel(TRIP_TYPE_OPTIONS, viewMarkup.trip_type) },
                  { label: "Cabin Class", value: optionLabel(CABIN_CLASS_OPTIONS, viewMarkup.cabin_class) },
                  { label: "Fare Type Code", value: viewMarkup.fare_type_code },
                  { label: "Markup", value: formatAmount(viewMarkup) },
                  { label: "Markup Applied On", value: viewMarkup.markup_applied_on },
                  { label: "Priority", value: viewMarkup.priority },
                  { label: "Total Commission", value: viewMarkup.total_commission },
                  { label: "Basic Commission", value: viewMarkup.basic_commission },
                  { label: "YQ Commission", value: viewMarkup.yq_commission },
                  { label: "Cashback Amount", value: viewMarkup.cashback_amount },
                  { label: "GST", value: viewMarkup.gst },
                  { label: "Service Charge", value: viewMarkup.service_charge },
                  { label: "Cancellation Fee", value: viewMarkup.cancellation_fee },
                  { label: "Change Fee", value: viewMarkup.change_fee },
                  { label: "Restriction On", value: viewMarkup.restriction_on },
                  { label: "Created At", value: viewMarkup.created_at },
                  { label: "Status", value: viewMarkup.status, badge: true },
                ]}
              />
            )}
          </Modal.Body>
        </Modal>
      </section>
    </>
  );
};

export default FlightMarkups;
